import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Index from "./pages/Index";
import { ScrollToTop } from "./components/ScrollToTop";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { CookieConsent } from "./components/CookieConsent";

// Lazy load pages for code splitting
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Produkti = lazy(() => import("./pages/Produkti"));
const Projekti = lazy(() => import("./pages/Projekti"));
const Kuhinje = lazy(() => import("./pages/Kuhinje"));
const ONas = lazy(() => import("./pages/ONas"));
const AbuDhabi = lazy(() => import("./pages/kitchens/AbuDhabi"));
const Adriatico = lazy(() => import("./pages/kitchens/Adriatico"));
const Maximus = lazy(() => import("./pages/kitchens/Maximus"));
const Orbita = lazy(() => import("./pages/kitchens/Orbita"));
const Leonardo = lazy(() => import("./pages/kitchens/Leonardo"));
const Pure = lazy(() => import("./pages/kitchens/Pure"));
const Royale = lazy(() => import("./pages/kitchens/Royale"));
const Imperial = lazy(() => import("./pages/kitchens/Imperial"));
const Offset = lazy(() => import("./pages/products/Offset"));
const Rosalia = lazy(() => import("./pages/products/Rosalia"));
const Rosa = lazy(() => import("./pages/products/Rosa"));
const Medusa = lazy(() => import("./pages/products/Medusa"));
const Norma = lazy(() => import("./pages/products/Norma"));
const Normus = lazy(() => import("./pages/products/Normus"));
const Erato = lazy(() => import("./pages/products/Erato"));
const Echo = lazy(() => import("./pages/products/Echo"));
const Voice = lazy(() => import("./pages/products/Voice"));
const Delta = lazy(() => import("./pages/products/Delta"));
const Terra = lazy(() => import("./pages/products/Terra"));
const Arctica = lazy(() => import("./pages/products/Arctica"));
const Milano = lazy(() => import("./pages/products/Milano"));
const Maestro = lazy(() => import("./pages/products/Maestro"));
const Uplift = lazy(() => import("./pages/products/Uplift"));
const Nubia = lazy(() => import("./pages/products/Nubia"));
const Nero = lazy(() => import("./pages/products/Nero"));
const Pocket = lazy(() => import("./pages/products/Pocket"));
const Zen = lazy(() => import("./pages/products/Zen"));
const Kyoto = lazy(() => import("./pages/products/Kyoto"));
const Halo = lazy(() => import("./pages/products/Halo"));
const Gem = lazy(() => import("./pages/products/Gem"));
const Cloud = lazy(() => import("./pages/products/Cloud"));
const Aria = lazy(() => import("./pages/products/Aria"));
const Sky = lazy(() => import("./pages/products/Sky"));
const Porta = lazy(() => import("./pages/products/Porta"));
const Clare = lazy(() => import("./pages/products/Clare"));
const Velvet = lazy(() => import("./pages/products/Velvet"));
const Focus = lazy(() => import("./pages/products/Focus"));
const Viva = lazy(() => import("./pages/products/Viva"));
const Basis = lazy(() => import("./pages/products/Basis"));
const Fluto = lazy(() => import("./pages/products/Fluto"));
const Verse = lazy(() => import("./pages/products/Verse"));
const Key = lazy(() => import("./pages/products/Key"));
const Noma = lazy(() => import("./pages/products/Noma"));
const Lago = lazy(() => import("./pages/products/Lago"));
const Radius = lazy(() => import("./pages/products/Radius"));
const Ronda = lazy(() => import("./pages/products/Ronda"));
const Frame = lazy(() => import("./pages/products/Frame"));
const Omnia = lazy(() => import("./pages/products/Omnia"));
const VetClinic = lazy(() => import("./pages/projects/VetClinic"));
const ApartmentU = lazy(() => import("./pages/projects/ApartmentU"));
const KitchenS = lazy(() => import("./pages/projects/KitchenS"));
const KitchenKT = lazy(() => import("./pages/projects/KitchenKT"));
const InterierL = lazy(() => import("./pages/projects/InterierL"));
const InterierUS = lazy(() => import("./pages/projects/InterierUS"));
const InterierMS = lazy(() => import("./pages/projects/InterierMS"));
const InterierBB = lazy(() => import("./pages/projects/InterierBB"));
const InterierMM = lazy(() => import("./pages/projects/InterierMM"));
const GhPortoroz = lazy(() => import("./pages/projects/GhPortoroz"));
const TcgPark = lazy(() => import("./pages/projects/TcgPark"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Projektiranje = lazy(() => import("./pages/Projektiranje"));
const IdejneZasnoveStanovanj = lazy(() => import("./pages/IdejneZasnoveStanovanj"));
const Emonika = lazy(() => import("./pages/projektiranje/Emonika"));
const SoseskaNova = lazy(() => import("./pages/projektiranje/SoseskaNova"));
const Nordika = lazy(() => import("./pages/projektiranje/Nordika"));
const JurckovoNaselje = lazy(() => import("./pages/projektiranje/JurckovoNaselje"));
const Reference = lazy(() => import("./pages/Reference"));
const Arhitektura = lazy(() => import("./pages/Arhitektura"));
const VillaIstria = lazy(() => import("./pages/arhitektura/VillaIstria"));
const Hoteli = lazy(() => import("./pages/Hoteli"));
const HoteliAlpe = lazy(() => import("./pages/hoteli/Alpe"));
const HoteliMediteran = lazy(() => import("./pages/hoteli/Mediteran"));
const HoteliPodezelje = lazy(() => import("./pages/hoteli/Podezelje"));
const HoteliMesto = lazy(() => import("./pages/hoteli/Mesto"));
const PoslovniProstori = lazy(() => import("./pages/PoslovniProstori"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Kalkulator = lazy(() => import("./pages/Kalkulator"));
const Cenik = lazy(() => import("./pages/Cenik"));
const VgradneOmarePoMeri = lazy(() => import("./pages/VgradneOmarePoMeri"));
const PravilaZasebnosti = lazy(() => import("./pages/PravilaZasebnosti"));
const SplosniPogoji = lazy(() => import("./pages/SplosniPogoji"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Helmet>
          <meta name="description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
          <meta property="og:title" content="Interoform – Kuhinje in mizarstvo po meri" />
          <meta property="og:description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
          <meta property="og:type" content="website" />
          <meta name="twitter:title" content="Interoform – Kuhinje in mizarstvo po meri" />
          <meta name="twitter:description" content="Interoform ustvarja ekskluzivno pohištvo in kuhinje po meri iz vrhunskih materialov. Mizarstvo za arhitekte in celovite rešitve za vaš dom." />
          <meta name="twitter:card" content="summary_large_image" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Interoform",
              url: "https://interoform.com",
              logo: "https://interoform.com/logo.png",
              description: "Mizarstvo in kuhinje po meri. Ekskluzivno pohištvo iz vrhunskih materialov.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Iška Loka 54",
                addressLocality: "Ig",
                postalCode: "1292",
                addressCountry: "SI",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+386-31-704-217",
                contactType: "customer service",
                email: "info@interoform.com",
                availableLanguage: "sl",
              },
            })}
          </script>
        </Helmet>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsTracker />
          <CookieConsent />
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/o-nas" element={<ONas />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/produkti" element={<Produkti />} />
              <Route path="/produkti/offset" element={<Offset />} />
              <Route path="/produkti/rosalia" element={<Rosalia />} />
              <Route path="/produkti/rosa" element={<Rosa />} />
              <Route path="/produkti/medusa" element={<Medusa />} />
              <Route path="/produkti/norma" element={<Norma />} />
              <Route path="/produkti/normus" element={<Normus />} />
              <Route path="/produkti/erato" element={<Erato />} />
              <Route path="/produkti/echo" element={<Echo />} />
              <Route path="/produkti/voice" element={<Voice />} />
              <Route path="/produkti/delta" element={<Delta />} />
              <Route path="/produkti/terra" element={<Terra />} />
              <Route path="/produkti/arctica" element={<Arctica />} />
              <Route path="/produkti/milano" element={<Milano />} />
              <Route path="/produkti/maestro" element={<Maestro />} />
              <Route path="/produkti/uplift" element={<Uplift />} />
              <Route path="/produkti/nubia" element={<Nubia />} />
              <Route path="/produkti/nero" element={<Nero />} />
              <Route path="/produkti/pocket" element={<Pocket />} />
              <Route path="/produkti/zen" element={<Zen />} />
              <Route path="/produkti/kyoto" element={<Kyoto />} />
              <Route path="/produkti/halo" element={<Halo />} />
              <Route path="/produkti/gem" element={<Gem />} />
              <Route path="/produkti/cloud" element={<Cloud />} />
              <Route path="/produkti/aria" element={<Aria />} />
              <Route path="/produkti/sky" element={<Sky />} />
              <Route path="/produkti/porta" element={<Porta />} />
              <Route path="/produkti/clare" element={<Clare />} />
              <Route path="/produkti/velvet" element={<Velvet />} />
              <Route path="/produkti/focus" element={<Focus />} />
              <Route path="/produkti/viva" element={<Viva />} />
              <Route path="/produkti/basis" element={<Basis />} />
              <Route path="/produkti/fluto" element={<Fluto />} />
              <Route path="/produkti/verse" element={<Verse />} />
              <Route path="/produkti/key" element={<Key />} />
              <Route path="/produkti/noma" element={<Noma />} />
              <Route path="/produkti/lago" element={<Lago />} />
              <Route path="/produkti/radius" element={<Radius />} />
              <Route path="/produkti/ronda" element={<Ronda />} />
              <Route path="/produkti/frame" element={<Frame />} />
              <Route path="/produkti/omnia" element={<Omnia />} />
              <Route path="/projekti" element={<Projekti />} />
              <Route path="/projekti/kuhinja-kt" element={<KitchenKT />} />
              <Route path="/projekti/kuhinja-s" element={<KitchenS />} />
              <Route path="/projekti/veterinarska-ambulanta" element={<VetClinic />} />
              <Route path="/projekti/stanovanje-u" element={<ApartmentU />} />
              <Route path="/projekti/interier-l" element={<InterierL />} />
              <Route path="/projekti/interier-us" element={<InterierUS />} />
              <Route path="/projekti/interier-ms" element={<InterierMS />} />
              <Route path="/projekti/interier-bb" element={<InterierBB />} />
              <Route path="/projekti/interier-mm" element={<InterierMM />} />
              <Route path="/projekti/gh-portoroz" element={<GhPortoroz />} />
              <Route path="/projekti/tcg-park" element={<TcgPark />} />
              <Route path="/kuhinje" element={<Kuhinje />} />
              <Route path="/kuhinje/abu-dhabi" element={<AbuDhabi />} />
              <Route path="/kuhinje/adriatico" element={<Adriatico />} />
              <Route path="/kuhinje/maximus" element={<Maximus />} />
              <Route path="/kuhinje/orbita" element={<Orbita />} />
              <Route path="/kuhinje/leonardo" element={<Leonardo />} />
              <Route path="/kuhinje/pure" element={<Pure />} />
              <Route path="/kuhinje/royale" element={<Royale />} />
              <Route path="/kuhinje/imperial" element={<Imperial />} />
              <Route path="/tehnologija" element={<Blog />} />
              <Route path="/tehnologija/:slug" element={<BlogPost />} />
              <Route path="/projektiranje" element={<Projektiranje />} />
              <Route path="/idejne-zasnove-stanovanj" element={<IdejneZasnoveStanovanj />} />
              <Route path="/projektiranje/emonika" element={<Emonika />} />
              <Route path="/projektiranje/soseska-nova" element={<SoseskaNova />} />
              <Route path="/projektiranje/nordika" element={<Nordika />} />
              <Route path="/projektiranje/jurckovo-naselje" element={<JurckovoNaselje />} />
              <Route path="/reference" element={<Reference />} />
              <Route path="/arhitektura" element={<Arhitektura />} />
              <Route path="/arhitektura/villa-istria" element={<VillaIstria />} />
              <Route path="/hoteli" element={<Hoteli />} />
              <Route path="/hoteli/alpe" element={<HoteliAlpe />} />
              <Route path="/hoteli/mediteran" element={<HoteliMediteran />} />
              <Route path="/hoteli/podezelje" element={<HoteliPodezelje />} />
              <Route path="/hoteli/mesto" element={<HoteliMesto />} />
              <Route path="/poslovni-prostori" element={<PoslovniProstori />} />
              <Route path="/pogosta-vprasanja" element={<FAQ />} />
              <Route path="/kalkulator" element={<Kalkulator />} />
              <Route path="/cenik" element={<Cenik />} />
              <Route path="/vgradne-omare-po-meri" element={<VgradneOmarePoMeri />} />
              <Route path="/pravila-zasebnosti" element={<PravilaZasebnosti />} />
              <Route path="/splosni-pogoji" element={<SplosniPogoji />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
